// @flow
// TODO: move it to common after booking bags 2.5 will be merged
import * as R from "ramda"

import Price from "common/records/Price"
import Journey from "common/records/Journey"
import mmbTrack from "mmb/services/tracking/mmbTrack"
import {
  BaggageWithoutCombinationReceived,
  BaggageModule,
  BaggagePriorityBoardingFiltered,
} from "mmb/services/tracking/constants/trackingEvents"
import {
  CATEGORY_API_CONVERTER,
  CONDITION_API_CONVERTER,
} from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingAddBags/services/constants"
import type { Baggage } from "mmb/scenes/AccountBooking/components/Booking/scenes/BookingAddBags/services/types"

export type BaggageCategoryApi = "hand_bag" | "hold_bag"
export type BaggageSubCategoryApi = "personal_item" | "cabin_bag" | "hold_bag"
type BaggageGroup = "adult" | "child" | "infant"

export type Conditions = {
  passenger_groups: BaggageGroup[],
  is_priority: string[],
}

type Combination = {
  price: {
    amount: number,
    base: number,
    currency: string,
    service: number,
    service_flat: number,
  },
  indices: number[],
  conditions: Conditions,
  passengers?: number[], // Just for save booking purposes
}

type Definition = {
  category: BaggageSubCategoryApi,
  price: {
    amount: number,
    base: number,
    currency: string,
    service: number,
    service_flat: number,
  },
  restrictions: {
    dimensions_sum: ?number,
    height: number,
    length: number,
    weight: number,
    width: number,
  },
  conditions: Conditions,
}

export type Combinations = {
  hand_bag: Combination[],
  hold_bag: Combination[],
}

export type PassengerCombinations = {
  hand_bag: { [passenger_id: string]: number },
  hold_bag: { [passenger_id: string]: number },
}

export type BaggageAvailableInput = {
  // Redefine definitions personalItem is actually personal_item in input
  offer: {
    definitions: {
      hand_bag: Definition[],
      hold_bag: Definition[],
    },
    combinations: Combinations,
  },
  passenger_combinations: PassengerCombinations,
}

const mapConditions = (conditions: Conditions): any =>
  Object.keys(conditions).reduce((acc, key) => {
    // $FlowFixMe
    acc[CONDITION_API_CONVERTER[key]] = conditions[key]
    return acc
  }, {})

const mapCombination = (combination: Combination, index: number): any => ({
  index,
  price: new Price({
    amount: Number(combination.price.amount),
    base: Number(combination.price.base),
    service: Number(combination.price.service),
    serviceFlat: Number(combination.price.service_flat),
    currency: combination.price.currency,
  }),
  indices: combination.indices,
  conditions: mapConditions(combination.conditions),
})

const mapDefinition = (definition: Definition): any => ({
  category: CATEGORY_API_CONVERTER[definition.category],
  price: new Price({
    amount: Number(definition.price.amount),
    base: Number(definition.price.base),
    service: Number(definition.price.service),
    serviceFlat: Number(definition.price.service_flat),
    currency: definition.price.currency,
  }),
  restrictions: {
    dimensionsSum: definition.restrictions.dimensions_sum,
    height: definition.restrictions.height,
    length: definition.restrictions.length,
    weight: definition.restrictions.weight,
    width: definition.restrictions.width,
  },
  conditions: mapConditions(definition.conditions),
})

const calculatePriority = (priorityAirlines: string[], journey, prioBoardingData) => {
  if (priorityAirlines.length <= 0) {
    return new Price({ amount: 0 })
  }

  const prices = priorityAirlines.map(airline => {
    const flightsPrices = journey
      .getFlightsByAirline(airline)
      .map(flight => flight.id)
      // $FlowFixMe
      .toArray()
      .map(flightId => {
        if (!prioBoardingData[flightId]) {
          return null
        }
        const priceInfo = prioBoardingData[flightId].price_info
        return new Price({
          amount: Number(priceInfo.amount),
          base: Number(priceInfo.base),
          currency: priceInfo.currency,
          merchant: Number(priceInfo.merchant),
          service: Number(priceInfo.service),
          serviceFlat: Number(priceInfo.service_flat),
        })
      })
      .filter(Boolean)
    if (flightsPrices.length <= 0) {
      return new Price({ amount: 0 })
    }
    return Price.sum(flightsPrices)
  })
  return Price.sum(prices)
}

const mapHandBagDefinition = (journey, prioBoardingData) => (definition: Definition): any => {
  const priorityAirlines = definition.conditions.is_priority || []
  if (priorityAirlines.length <= 0) {
    // No need for priority price
    return mapDefinition(definition)
  }
  const priorityPrice = calculatePriority(priorityAirlines, journey, prioBoardingData)
  const def = mapDefinition(definition)
  return { ...def, price: Price.sum([def.price, priorityPrice]) }
}

const mapHandBagCombination = (journey, prioBoardingData) => (
  combination: Combination,
  index: number,
): any => {
  const priorityAirlines = combination.conditions.is_priority || []
  if (priorityAirlines.length <= 0) {
    // No need for priority price
    return mapCombination(combination, index)
  }
  const priorityPrice = calculatePriority(priorityAirlines, journey, prioBoardingData)
  const comb = mapCombination(combination, index)
  return { ...comb, price: Price.sum([comb.price, priorityPrice]) }
}

const handBagCombinationWithoutPriority = (combination, index, selected): any => {
  const priorityAirlines = combination.conditions.is_priority || []
  return priorityAirlines.length <= 0 || (priorityAirlines.length > 0 && index === selected)
}

export const mapperBaggage = (
  input: BaggageAvailableInput,
  journey: Journey,
  //  additionalServices: ?AdditionalServicesInput,
  additionalServices: ?any,
): ?Baggage => {
  const priorityBoarding = additionalServices ? additionalServices.priority_boarding : null
  const prioBoardingData = priorityBoarding ? priorityBoarding.data : {}

  if (!input) {
    return null
  }

  const hasBaggageWithoutCombination = R.pipe(
    R.values,
    R.any(R.equals(-1)),
  )

  if (
    hasBaggageWithoutCombination(input.passenger_combinations.hand_bag) ||
    hasBaggageWithoutCombination(input.passenger_combinations.hold_bag)
  ) {
    mmbTrack({ module: BaggageModule, action: BaggageWithoutCombinationReceived })
  }

  const filteredPriorityBoardings = Object.keys(input.passenger_combinations.hand_bag).map(
    paxId => {
      const handBag = input.passenger_combinations.hand_bag[paxId]
      const filteredHandCombination = input.offer.combinations.hand_bag.every((comb, i) =>
        handBagCombinationWithoutPriority(comb, i, handBag),
      )
      return !filteredHandCombination
    },
  )

  if (filteredPriorityBoardings.some(f => f)) {
    mmbTrack({ module: BaggageModule, action: BaggagePriorityBoardingFiltered })
  }

  return {
    definitions: {
      handBag: input.offer.definitions.hand_bag.map(
        mapHandBagDefinition(journey, prioBoardingData),
      ),
      holdBag: input.offer.definitions.hold_bag.map(mapDefinition),
    },
    combinations: {
      handBag: input.offer.combinations.hand_bag.map(
        mapHandBagCombination(journey, prioBoardingData),
      ),
      holdBag: input.offer.combinations.hold_bag.map(mapCombination),
    },
    passengerCombinations: {
      handBag: input.passenger_combinations.hand_bag,
      holdBag: input.passenger_combinations.hold_bag,
    },
    selectedPassenger: null,
    passengerSelectedCombinations: {
      handBag: input.passenger_combinations.hand_bag,
      holdBag: input.passenger_combinations.hold_bag,
    },
  }
}
